import React from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { components } from 'react-select';
import {
  setCalibrationScoreSettings,
  setGridDefinition,
  useCalibrationScoreSettings,
  useGridDefinitionMetaData,
} from 'reducers/reviewCreationSlice';
import { generateRows } from 'screens/admin/Reviews/CalibrationSettings/common/helper';

import { OrderedList, Text, ViewStar } from 'assets/assetsV1/iconComponents';
import { SettingsThinOutline } from 'assets/assetsV1/iconComponents/SettingsThinOutline';

import {
  ExpandablePaper,
  ExpandablePaperBody,
  ExpandablePaperHeader,
} from 'uiToolkitV1/ExpandablePaper/ExpandablePaper';
import { SelectDashed } from 'uiToolkitV1/Select';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextBodyMedium, TextHeader1, TextLabel } from 'uiToolkitV1/Typography';

import { TextScoreSettingTable } from './TextScoreSettingTable';

export const AnswerTypes = {
  DROP_DOWN: 'DROP_DOWN',
  NUMBER_RATING: 'NUMBER_RATING',
};
export const AnswerTypesLabel = {
  DROP_DOWN: 'Text options',
  NUMBER_RATING: 'Numeric score',
};

const calculateGridByMaxValue = ({ newMaxScore, rowsCount, columnsCount }) => {
  const newGrid = generateRows({ rows: rowsCount, cols: columnsCount, maxScore: newMaxScore });
  return newGrid;
};

const ScoreRatingIcon = () => (
  <Flex
    h="40px"
    w="40px"
    minW="40px"
    borderRadius="50%"
    bg="feedback.DBF2D3"
    alignItems="center"
    justifyContent="center"
  >
    <ViewStar boxSize="20px" color="functional.1A5724" />
  </Flex>
);
const ScorePreview = () => {
  const { answerType, answerOptions } = useCalibrationScoreSettings();
  return (
    <Flex
      w="350px"
      borderRadius="8px"
      border="1px solid"
      borderColor="surface.F5F7F9"
      bg="surface.FDFDFD"
      p="3"
      flexDir="column"
      alignItems="center"
      minH="150px"
    >
      <TextBodyMedium mb="2px" p={2}>
        This is a preview of your score
      </TextBodyMedium>
      <Flex flexGrow={1} flexDir="column" justifyContent="center" alignItems="center">
        <TextBodyMedium bold color="functional.1A5724" mb={4}>
          Overall performance score is
        </TextBodyMedium>

        {answerType === 'NUMBER_RATING' && (
          <Flex justifyContent="center">
            <ScoreRatingIcon />
            <Flex alignItems="baseline">
              <TextHeader1 bold ml={3} color="functional.1A5724">
                {Number(answerOptions.length - 1).toFixed(0)}
              </TextHeader1>
              <TextBodyMedium color="functional.1A5724" bold mx="2px">
                /
              </TextBodyMedium>
              <TextBodyMedium color="functional.1A5724" position="relative" bottom="1px" bold>
                {Number(answerOptions.length).toFixed(0)}
              </TextBodyMedium>
            </Flex>
          </Flex>
        )}
        {answerType === 'DROP_DOWN' && (
          <Flex justifyContent="center" alignItems="center" overflow="hidden" wordBreak="break-word">
            <ScoreRatingIcon />
            <TextHeader1 bold ml={3} color="functional.1A5724">
              {answerOptions[answerOptions.length - 1]?.description}
            </TextHeader1>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

const TextOptionsSettings = ({ updateGridHandler }) => {
  const { answerOptions } = useCalibrationScoreSettings();
  const dispatch = useDispatch();

  const onChangeHandler = (newAnswerOptions) => {
    dispatch(
      setCalibrationScoreSettings({
        answerOptions: newAnswerOptions,
      })
    );
    updateGridHandler(newAnswerOptions);
  };

  return (
    <Box px={1}>
      <TextScoreSettingTable scoreSetting={answerOptions} onChange={onChangeHandler} />
    </Box>
  );
};

const defaultTextAnswerOptions = [
  {
    description: 'Below expectations',
    score: 1,
  },
  {
    description: 'Meets expectations',
    score: 2,
  },
  {
    description: 'Exceeds expectations',
    score: 3,
  },
  {
    description: 'Far exceeds expectations',
    score: 4,
  },
  {
    description: 'Outstanding',
    score: 5,
  },
];

const getOptionObject = (score) => ({
  description: '',
  displayOrder: score,
  score,
});

const defaultNumericOptions = [
  {
    description: '',
    displayOrder: 1,
    score: 1,
  },
  {
    description: '',
    displayOrder: 2,
    score: 2,
  },
  {
    description: '',
    displayOrder: 3,
    score: 3,
  },
  {
    description: '',
    displayOrder: 4,
    score: 4,
  },
  {
    description: '',
    displayOrder: 5,
    score: 5,
  },
];

const NumericOptionSettings = ({ updateGridHandler }) => {
  const { answerOptions } = useCalibrationScoreSettings();
  const dispatch = useDispatch();

  const options = [3, 4, 5, 6, 7, 8, 9, 10].map((i) => ({
    value: i,
    label: i,
  }));

  const onChangeHandler = (selectedOption) => {
    const newAnswerOptions = Array.from({ length: selectedOption.value }, (_, i) => getOptionObject(i + 1));
    dispatch(
      setCalibrationScoreSettings({
        answerOptions: newAnswerOptions,
      })
    );
    updateGridHandler(newAnswerOptions);
  };

  return (
    <Flex p="10px 0 10px 8px" alignItems="center">
      <TextBodyMedium mr={6}>Select a Max Score</TextBodyMedium>
      <Box flexGrow={1}>
        <SelectDashed
          options={options}
          value={options.filter((o) => o.value === answerOptions[answerOptions.length - 1].score)[0]}
          handleChange={onChangeHandler}
        />
      </Box>
    </Flex>
  );
};

export const Option = (props) => {
  const { Option } = components;
  const {
    data: { Icon, label },
  } = props;
  return (
    <Option {...props}>
      <Flex alignItems="center" sx={{ color: '#333' }}>
        <Icon boxSize="18px" mr={2} color="icon.g2" />
        {label}
      </Flex>
    </Option>
  );
};

export const SingleValue = (props) => {
  const { SingleValue } = components;
  const {
    data: { Icon, label },
  } = props;
  return (
    <SingleValue {...props}>
      <Flex alignItems="center" sx={{ color: '#333' }}>
        <Icon boxSize="18px" mr={2} color="icon.g2" />
        {label}
      </Flex>
    </SingleValue>
  );
};

export const ScoreSection = () => {
  const { answerType } = useCalibrationScoreSettings();
  // this is required here because changing the answerOptions will change the gridDefinition
  const { rowsCount, columnsCount } = useGridDefinitionMetaData();

  const dispatch = useDispatch();
  const ratingTypeOptions = Object.keys(AnswerTypes).map((option) => ({
    value: option,
    label: AnswerTypesLabel[option],
    Icon: option === AnswerTypes.DROP_DOWN ? Text : OrderedList,
  }));

  const onChangeHandler = (selectedOption) => {
    const newAnswerOptions =
      selectedOption.value === AnswerTypes.DROP_DOWN ? defaultTextAnswerOptions : defaultNumericOptions;
    dispatch(
      setCalibrationScoreSettings({
        answerType: selectedOption.value,
        answerOptions: newAnswerOptions,
      })
    );
    updateGridHandler(newAnswerOptions);
  };

  const updateGridHandler = (newAnswerOptions) => {
    const maxValue = newAnswerOptions?.[newAnswerOptions?.length - 1].score;
    const newGrid = calculateGridByMaxValue({ rowsCount, columnsCount, newMaxScore: maxValue });
    dispatch(setGridDefinition(newGrid));
  };

  return (
    <ExpandablePaper>
      <ExpandablePaperHeader
        title="Set the Overall Score"
        leftIconComponent={<SettingsThinOutline boxSize="24px" color="icon.g2" />}
      />
      <ExpandablePaperBody>
        <Flex alignItems="center" p="6px 8px">
          <TextBodyMedium mr={2} mb="2px">
            Configure how the overall score will be calculated and displayed
          </TextBodyMedium>

          <TooltipInfoV1 title="In case the rating scales for each of the elements in the templates are different all scores will be first rebased and then calculated to get the overall score based on the scale selected in this section" />
        </Flex>
        <Flex justifyContent="space-between" alignItems="stretch" p="8px 2px">
          <Box id="left container" minW="515px">
            <Box pl="8px">
              <TextBodyMedium mb="1">Score Type:</TextBodyMedium>
              <SelectDashed
                options={ratingTypeOptions}
                value={answerType ? ratingTypeOptions.filter((o) => o.value === answerType)[0] : ratingTypeOptions[0]}
                handleChange={onChangeHandler}
                components={{ Option, SingleValue }}
              />
              {answerType === AnswerTypes.DROP_DOWN && (
                <TextLabel color="text.g2" mt={1}>
                  Selecting this option will display the specified text to users based on the defined score ranges.
                </TextLabel>
              )}
            </Box>

            {answerType === AnswerTypes.DROP_DOWN && <TextOptionsSettings updateGridHandler={updateGridHandler} />}
            {answerType === AnswerTypes.NUMBER_RATING && (
              <NumericOptionSettings updateGridHandler={updateGridHandler} />
            )}
          </Box>

          <ScorePreview />
        </Flex>
      </ExpandablePaperBody>
    </ExpandablePaper>
  );
};
